/**
 * RimLoadingHint — "Loading…" pulse above the MenuBar pill.
 *
 * Rendered in Viewer.tsx as a standalone absolutely-positioned element.
 * Only visible while `rimLoading === true` AND the rim sub-menu is open.
 *
 * Positioning constants (keep in sync with MenuBar.tsx):
 *   MENU_PILL_BOTTOM_PX  — bottom-5 = 20px (Tailwind)
 *   MENU_PILL_HEIGHT_PX  — h-13 = 52px (Tailwind)
 *   MENU_PILL_LIFT_PX    — translateY(-77px) when panel is open
 *   Total pill-top ≈ 20 + 77 = 97px from bottom; we add 8px gap = 105px.
 *   Scale(0.78) shrinks the pill but not the viewport position, so the
 *   hint visually clears the scaled pill at this offset.
 */
import { useAppStore } from '../../store/app.store';

/**
 * Distance from the bottom of the viewport to place the loading hint,
 * so it floats above the scaled MenuBar pill when a panel is open.
 * Derived from: pill bottom (20px) + lift (77px) + pill height×0.78/2 (20px) + gap (8px) ≈ 125px.
 * Round up to 130px for comfortable visual clearance.
 */
const HINT_BOTTOM_PX = 130;

interface RimLoadingHintProps {
  /** Only show when the rim panel is active */
  visible: boolean;
  /** Translated label, e.g. "Laden…". Falls back to "Loading…". */
  label?: string;
}

export function RimLoadingHint({ visible, label = 'Loading\u2026' }: RimLoadingHintProps) {
  const rimLoading = useAppStore((s) => s.rimLoading);

  if (!visible || !rimLoading) return null;

  return (
    <div
      className="absolute left-1/2 z-100 pointer-events-none"
      style={{
        bottom: `${HINT_BOTTOM_PX}px`,
        transform: 'translateX(-50%)',
      }}
      aria-live="polite"
    >
      <span
        className="text-xs font-medium text-white/60 tracking-wide"
        style={{ animation: 'rimLoadingPulse 1.2s ease-in-out infinite' }}
      >
        {label}
      </span>

      <style>{`
        @keyframes rimLoadingPulse {
          0%, 100% { opacity: 0.4; }
          50%      { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
